// src/components/admin/ServicesManagement.js
import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Modal,
  Spinner,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useData } from "../../providers/dataProvider";
import { pageMeta } from "../../config/helmetConfig";
import { updateMetaTags } from "../../utils/metaManager";

function ModalContent({
  serviceToDelete,
  setShowDeleteModal,
  handleDeleteService,
  servicesLoading,
}) {
  if (servicesLoading) {
    return (
      <Modal.Body className="bg-dark text-white">
        <div className="d-flex justify-content-center align-items-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Modal.Body>
    );
  }
  return (
    <>
      <Modal.Header className="bg-dark text-white border-secondary">
        <Modal.Title>Confirm Delete</Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-dark text-white">
        Are you sure you want to delete the service:
        <strong> {serviceToDelete?.title}</strong>?
        <br />
        This action cannot be undone.
      </Modal.Body>
      <Modal.Footer className="bg-dark text-white border-secondary">
        <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
          Cancel
        </Button>
        <Button variant="danger" onClick={handleDeleteService}>
          Delete Service
        </Button>
      </Modal.Footer>
    </>
  );
}

export default function AdminServices() {
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const { services, removeService, servicesLoading } = useData();
  const navigate = useNavigate();
  useEffect(() => {
    updateMetaTags(pageMeta.admin);
  }, []);

  const handleEditService = (id) => {
    navigate(`/admin/services/edit/${id}`);
  };

  const confirmDelete = (service) => {
    setServiceToDelete(service);
    setShowDeleteModal(true);
  };

  const handleDeleteService = async () => {
    try {
      await removeService(serviceToDelete.id);
    } catch (error) {
      setError(error.message);
    } finally {
      setShowDeleteModal(false);
    }
  };

  return (
    <Container className="py-4">
      <Row className="mb-4">
        <Col className="d-flex justify-content-between align-items-center">
          <h1 className="text-light">Manage Services</h1>
          <Button
            variant="outline-light"
            size="sm"
            onClick={() => navigate("/admin/dashboard")}
          >
            Back to Dashboard
          </Button>
        </Col>
      </Row>

      {error && (
        <Row className="mb-4">
          <Col>
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          </Col>
        </Row>
      )}

      <Row>
        <Col>
          <Card
            className="bg-dark text-white border-secondary"
            style={{ width: "100%" }}
          >
            <Card.Body>
              <div className="table-responsive">
                <Table variant="dark" bordered hover>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Service Name</th>
                      <th className="d-none d-md-table-cell">Description</th>
                      <th style={{ width: "140px" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {services.map((service, index) => (
                      <tr key={service.id}>
                        <td>{service.id}</td>
                        <td>{service.title}</td>
                        <td className="d-none d-md-table-cell">
                          {service.description}
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleEditService(service.id)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => confirmDelete(service)}
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
            <Card.Footer className="bg-dark border-secondary">
              <Button
                variant="success"
                onClick={() => navigate("/admin/services/edit/new")}
              >
                Add New Service
              </Button>
            </Card.Footer>
          </Card>
        </Col>
      </Row>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        backdrop="static"
        keyboard={false}
        centered
      >
        <ModalContent
          serviceToDelete={serviceToDelete}
          setShowDeleteModal={setShowDeleteModal}
          handleDeleteService={handleDeleteService}
          servicesLoading={servicesLoading}
        />
      </Modal>
    </Container>
  );
}
